import * as Sentry from '@sentry/vue'

import { treatment } from '@/modules/abPlatform'

import { APP_AREAS } from './areas'
import experimentsConfig from './experiments'
import { ENTRY_POINTS } from './entryPoints'
import { findMatchingOverride } from '../helpers/jsonConfig'

import { AppArea, AppAreaConfig, DomainMatch, DomainsConfig, OverridesParams } from './@types'
import { generateDomainConfig, isAppAreaConfig } from './funnelUtils'

const areaBySlug = new Map(APP_AREAS.map((area) => [area.appArea, area]))

/**
 * Manual-pathnames, заданные точками входа: путь → area. Благодаря этому
 * префикс-резолвинг (`getFunnelPrefixFromPath` → `pathnames[prefix]`) понимает
 * `/app` → glp-app, как понимает area-пути вроде `/glp-app`.
 */
const entryPathnames: { [pathname: string]: AppAreaConfig } = {}
for (const entry of ENTRY_POINTS) {
	const area = areaBySlug.get(entry.area)
	if (area) {
		entryPathnames[entry.path] = area
	}
}

/**
 * Area по умолчанию для нефанельных страниц (404, /redirect, интейк, intake-done) и
 * fallback'ов. Раньше жила в `pathnames['/']`; теперь `/` отдаёт 404, поэтому
 * дефолт берём из основной (первой) точки входа.
 */
const DEFAULT_AREA = areaBySlug.get(ENTRY_POINTS[0].area)
if (!DEFAULT_AREA) {
	throw new Error(`Entry point area "${ENTRY_POINTS[0].area}" is not registered in APP_AREAS`)
}

const allFunnels = generateDomainConfig(APP_AREAS, entryPathnames)

const sharedFunnels = allFunnels

const config: DomainsConfig = {
	'trykova.com': {
		experiments: true,
		pathnames: sharedFunnels,
	},
}

/**
 * Array of domain configurations with their corresponding regex patterns.
 * Used for matching hostnames and retrieving domain-specific settings.
 *
 * @type {DomainMatch[]}
 *
 * @property {Object} domainConfig - Configuration for the domain
 * @property {boolean} domainConfig.experiments - Whether experiments are enabled for this domain
 * @property {Object} domainConfig.pathnames - Object containing path configurations and service prefixes
 * @property {RegExp} regex - Regular expression for matching the domain pattern
 *
 * @example
 * // Example of a domain match object:
 * {
 *   domainConfig: {
 *     experiments: true,
 *     pathnames: { '/app': {...}, '/glp-app': {...} }
 *   },
 *   regex: /^trykova\.com$/
 * }
 */
export const DOMAINS_FROM_CONFIG = Object.entries(config).reduce<DomainMatch[]>((result, [domain, domainConfig]) => {
	return [
		...result,
		{
			domainConfig: { ...domainConfig, pathnames: { ...domainConfig.pathnames } },
			regex: new RegExp(`^${domain.replace(/^\*/, '.*')}$`),
		},
	]
}, [])

/**
 * Array of tuples containing URL path prefixes and their corresponding regex patterns.
 * Used for validating and matching URL paths across different domains. Built solely
 * from the domain configs' pathname keys (area paths + entry points) — no service
 * prefixes.
 *
 * @type {[string, RegExp][]}
 *
 * @example
 * // Returns array like:
 * [
 *   ['/app', /^\/app(?:\/[^-][^/]*)*\/?$/],
 *   ['/glp-app', /^\/glp-app(?:\/[^-][^/]*)*\/?$/]
 * ]
 */
export const DOMAINS_PREFIXES = [
	...new Set(
		Object.values(config)
			.map((domainConfig) => Object.keys(domainConfig.pathnames))
			.flat()
			.filter((prefix) => prefix !== '/'),
	),
].map((prefix) => [prefix, new RegExp(`^${prefix}(?:/[^-][^/]*)*/?$`)])

type ExperimentPart = 'landing' | 'funnel' | 'paywall'

/**
 * Достаёт конфиг-часть активного эксперимента для app area — тот же контракт, что в
 * simple-performance-web, но поверх ин-хаус AB-платформы (`treatment()` из
 * `@/modules/abPlatform`) вместо снятого ABsmartly.
 *
 * Реестр: `src/funnels/experiments.ts` → `[appArea][experimentName]` = массив вариантов,
 * где индекс 0 — вариант B (бекендный `treatment() === 1`), 1 — C и так далее. Контроль
 * (`0`) конфигом не описывается: он и есть базовая схема.
 *
 * ВАЖНО про экспозицию. У нашего SDK нет раздельных `assign()` / `treatment()` — один вызов
 * и назначает вариант, и шлёт `ExperimentExposure`. Поэтому эксперименты, ни один вариант
 * которых не трогает запрошенную `part`, отсеиваются ПО КОНФИГУ, до вызова `treatment()`:
 * иначе пейвольный эксперимент получал бы экспозицию ещё на лендинге и ломал знаменатель
 * своей метрики. Обратная сторона: если на одну area повесить два эксперимента, трогающих
 * одну и ту же часть, экспозиция уедет обоим (в SPW на этом месте стоит
 * `TODO experiments intersection handler` — здесь та же нерешённая задача).
 *
 * @param {AppArea} appArea - Область воронки (например, 'glp-dm')
 * @param {'landing' | 'funnel' | 'paywall'} part - Какую часть конфига вернуть
 *
 * @returns {Object|null} Конфиг-часть активного варианта, либо null (контроль / нет эксперимента)
 *
 * @example
 * getAppAreaExperimentPart('glp-dm', 'paywall')?.experiment
 */
export const getAppAreaExperimentPart = (appArea: AppArea, part: ExperimentPart) => {
	const appAreaExperiments = experimentsConfig[appArea]

	if (!appAreaExperiments) {
		return null
	}

	for (const experimentName of Object.keys(appAreaExperiments)) {
		const variants = appAreaExperiments[experimentName]

		// Статическая проверка — без неё `treatment()` ниже поставил бы экспозицию всем
		// экспериментам area, включая те, что к этой части отношения не имеют.
		if (!variants?.some((variant) => variant?.[appArea]?.[part])) {
			continue
		}

		const treatmentIndex = treatment(experimentName)

		if (treatmentIndex <= 0) {
			continue
		}

		const areaConfig = variants[treatmentIndex - 1]?.[appArea]

		if (!areaConfig) {
			// Вариант есть на платформе, но не в коде — например, D завели в админке раньше
			// релиза. Отдаём контроль, а не падаем, но сообщаем: сплит уже разъехался.
			Sentry.captureMessage(`Experiment ${experimentName}: no config for variant ${treatmentIndex}`, {
				level: 'error',
				tags: { experiment: experimentName, appArea },
			})
			continue
		}

		if (areaConfig[part]) {
			return areaConfig[part]
		}
	}

	return null
}

/**
 * Applies overrides to a specific part of the area configuration (paywall or funnel).
 * Used to customize configuration based on country
 *
 * @param {AppAreaConfig | { appArea: string }} areaConfig - Base configuration for the area
 * @param {'paywall' | 'funnel'} configKey - The configuration key to apply overrides to
 * @param {OverridesParams} params - Parameters for override conditions
 * @param {string} params.country - Country code to check overrides against
 *
 * @returns {AppAreaConfig} Modified or original area configuration
 */
const applyConfigOverrides = (
	areaConfig: AppAreaConfig | { appArea: string },
	configKey: 'paywall' | 'funnel',
	{ country }: OverridesParams,
): AppAreaConfig => {
	if (isAppAreaConfig(areaConfig)) {
		const configSection = areaConfig[configKey]

		if (configSection && Array.isArray(configSection.overrides)) {
			const matchingOverride = findMatchingOverride(configSection.overrides, { country })

			if (matchingOverride && typeof matchingOverride === 'object' && !Array.isArray(matchingOverride)) {
				// Хотим записать только свойства из override, без matcher
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { matcher: _, ...overrideData } = matchingOverride
				return {
					...areaConfig,
					[configKey]: {
						...configSection,
						...overrideData,
					},
				}
			}
		}
	}

	// eslint-disable-next-line no-restricted-syntax
	return areaConfig as AppAreaConfig
}

/**
 * Applies funnel experiment configuration to the base area configuration if an active experiment exists.
 * ABSmartly removed — always returns the original config.
 *
 * @param {AppAreaConfig | { appArea: string }} areaConfig - Base configuration for the area
 *
 * @returns {AppAreaConfig} Original area configuration
 */
const applyFunnelExperiments = (areaConfig: AppAreaConfig | { appArea: string }): AppAreaConfig => {
	// eslint-disable-next-line no-restricted-syntax
	return areaConfig as AppAreaConfig
}

/**
 * Defines a funnel configuration based on either app area or initial path.
 * If appArea is provided, finds configuration by matching app area.
 * Otherwise, uses initialPath to find configuration in domain's pathnames.
 *
 * @param {Object} params - Parameters for funnel definition
 * @param {string|null} params.appArea - App area identifier (e.g., 'female', 'activity-female-yoga').
 *                                      Required if initialPath is not provided
 * @param {string|null} params.initialPath - URL path to match against domain's pathname configurations.
 *                                          Required if appArea is not provided
 * @param {string} params.country - Country code for applying country-specific overrides
 * @param {string} params.domain - Domain name to match against domain configurations
 *
 * @returns {AppAreaConfig} Funnel configuration with applied overrides and experiments
 *
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defineFunnelByParams(params: any) {
	const { appArea, country, domain, initialPath } = params
	let currentAreaConfig

	if (appArea) {
		const domainConfig = DOMAINS_FROM_CONFIG.find((item) => item.regex.test(domain))?.domainConfig
		currentAreaConfig = domainConfig
			? Object.values(domainConfig.pathnames).find((v) => v.appArea === appArea)
			: undefined
	} else if (initialPath) {
		const domainConfig = DOMAINS_FROM_CONFIG.find((item) => item.regex.test(domain))?.domainConfig

		// initialPath `/` (теперь 404) и любой неизвестный путь → дефолтная area,
		// без Sentry-шума: ветка `!currentAreaConfig` ниже ловит только реально
		// неизвестные area (например, `?area=` с несуществующим slug).
		currentAreaConfig = domainConfig?.pathnames[initialPath] || DEFAULT_AREA
	}

	if (!currentAreaConfig) {
		Sentry.captureMessage(`No funnel found for this ${appArea ? 'app area' : 'domain'}`, {
			tags: { funnelTypeError: true },
			extra: params,
		})

		currentAreaConfig = getDefaultAppAreaConfigByHostname(domain)
	}

	currentAreaConfig = applyConfigOverrides(currentAreaConfig, 'funnel', { country })
	currentAreaConfig = applyConfigOverrides(currentAreaConfig, 'paywall', { country })

	currentAreaConfig = applyFunnelExperiments(currentAreaConfig)

	return currentAreaConfig
}

// `/` теперь отдаёт 404, поэтому дефолтная area больше не живёт в
// `pathnames['/']`. Возвращаем area основной точки входа. Параметр сохранён для
// совместимости с вызывающим кодом и под будущие per-domain дефолты.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getDefaultAppAreaConfigByHostname = (_hostname: string) => {
	return DEFAULT_AREA
}
